import { getHexTileMapKey } from '../../../hexmap/model/keys';
import type { RuleSet } from '../../../rules';
import { computeVision, type PlayerId, type SimState } from '../../../sim';
import { targetOf } from '../text';
import type { GameEvent } from '../types';

/**
 * Players `playerId` sees for the first time: one event per rival with a
 * unit or building on a currently visible tile. Adds them to `met`.
 * `vision` gives the tiles in sight, asked only when needed (unmet rivals
 * left); it defaults to the vision `state` gives: pass the real one when
 * `state` is the viewer's (a rival spy disguised as an own worker sees nothing).
 */
export function discoveryEvents(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  met: Set<PlayerId>,
  vision?: () => ReadonlySet<string>,
  playerName: (id: PlayerId) => string = (id) => id
): GameEvent[] {
  const unmet = Object.keys(state.players).filter((id) => id !== playerId && !met.has(id));
  if (unmet.length === 0) return [];
  const visible = vision ? vision() : computeVision(state, rules, playerId);
  const events: GameEvent[] = [];
  for (const e of [...Object.values(state.buildings), ...Object.values(state.units)]) {
    if (e.owner === playerId || met.has(e.owner) || !state.players[e.owner]) continue;
    if ('inside' in e && e.inside) continue; // Hidden inside a building.
    if (!visible.has(getHexTileMapKey(e.pos))) continue;
    met.add(e.owner);
    events.push({
      kind: 'discovery',
      text: `You found ${playerName(e.owner)}`,
      pos: { ...e.pos },
      target: targetOf(rules, e, playerName),
    });
  }
  return events;
}
